/**
 * ChauffeurMobileLayout — EPIC 1 (T1-T4) + EPIC 2 (T13/T14/T16)
 * Mobile-first layout shell for the entire Espace Chauffeur.
 * Provides: fixed header + sticky active-mission button + fixed bottom nav.
 * PWA: offline queue integration, standalone mode detection, install prompt.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DriverPendingOverlay from '../driver/DriverPendingOverlay';
import GpsIndicator from './GpsIndicator';
import GpsLossBanner from './GpsLossBanner';
import NetworkBanner from './NetworkBanner';
import { useGpsTracking } from '../../hooks/useGpsTracking';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';
import { useStandaloneMode } from '../../hooks/useStandaloneMode';
import { LogOut, User, CalendarDays, Compass, Navigation, PlayCircle, Download, MessageSquare, Wallet, Clock, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = config.API_URL;

const NAV_ITEMS = [
  { to: '/chauffeur', label: 'Missions', icon: CalendarDays, end: true },
  { to: '/chauffeur/shift', label: 'Shift', icon: Clock },
  { to: '/chauffeur/excursions', label: 'Excursions', icon: Compass },
  { to: '/chauffeur/wallet', label: 'Wallet', icon: Wallet },
  { to: '/chauffeur/profil', label: 'Profil', icon: User },
];

export default function ChauffeurMobileLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(user?.online || false);
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [activeMission, setActiveMission] = useState(null);
  const [pendingProposals, setPendingProposals] = useState(0);
  const [sessionReplaced, setSessionReplaced] = useState(false);
  const sessionPollRef = useRef(null);

  // GPS Tracking (EPIC 1)
  const gps = useGpsTracking();

  // Network Status (EPIC 8 - Ticket 17)
  const network = useNetworkStatus();

  // T13/T14: Offline GPS Queue
  const offlineQueue = useOfflineQueue();

  // T16: Standalone mode detection
  const standalone = useStandaloneMode();

  // Single-device session enforcement: poll every 30s
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    const checkSession = async () => {
      try {
        await axios.get(`${API}/api/auth/session-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        if (err?.response?.status === 401 && err?.response?.data?.detail === 'SESSION_REPLACED') {
          setSessionReplaced(true);
          clearInterval(sessionPollRef.current);
          // Auto-logout after 4 seconds
          setTimeout(() => {
            logout();
            navigate('/chauffeur/login', { replace: true });
          }, 4000);
        }
      }
    };

    checkSession();
    sessionPollRef.current = setInterval(checkSession, 30000);
    return () => clearInterval(sessionPollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // T13: Connect offline queue to GPS tracking
  useEffect(() => {
    gps.setEnqueueFn(offlineQueue.enqueue);
  }, [gps.setEnqueueFn, offlineQueue.enqueue]);

  // Ticket 10/13: Redirect non-ACTIVE drivers to profil page
  const driverStatus = user?.driver_status;
  const isActiveDriver = driverStatus === 'ACTIVE';
  const isOnProfilPage = location.pathname === '/chauffeur/profil';
  const isOnWalletPage = location.pathname === '/chauffeur/wallet';

  useEffect(() => {
    if (!isActiveDriver && !isOnProfilPage && !isOnWalletPage) {
      navigate('/chauffeur/profil', { replace: true });
    }
  }, [isActiveDriver, isOnProfilPage, isOnWalletPage, navigate]);

  // Poll pending mission proposals
  useEffect(() => {
    if (!isActiveDriver) return;
    const token = localStorage.getItem('access_token');
    const fetchPending = () => {
      axios.get(`${API}/api/dispatch/pro/chauffeur/missions/pending`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setPendingProposals(r.data?.total || 0))
        .catch(() => {});
    };
    fetchPending();
    const iv = setInterval(fetchPending, 30000);
    return () => clearInterval(iv);
  }, [isActiveDriver]);

  // Fetch online status + auto-start tracking if already online
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios
      .get(`${API}/api/driver/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setIsOnline(res.data.online);
        // If driver is already online, resume GPS tracking
        if (res.data.online && gps.gpsStatus === 'inactive') {
          gps.startTracking();
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ticket 4: Poll for active mission
  const fetchActiveMission = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await axios.get(`${API}/api/chauffeur/dashboard/current-mission`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveMission(res.data.mission || null);
    } catch {
      setActiveMission(null);
    }
  }, []);

  useEffect(() => {
    fetchActiveMission();
    const interval = setInterval(fetchActiveMission, 30000);
    return () => clearInterval(interval);
  }, [fetchActiveMission]);

  // Re-fetch on route change (driver may have updated status)
  useEffect(() => {
    fetchActiveMission();
  }, [location.pathname, fetchActiveMission]);

  // T1: Toggle ONLINE requires GPS permission; T4: Stop tracking on OFFLINE
  const toggleOnline = async () => {
    setTogglingOnline(true);
    const token = localStorage.getItem('access_token');
    try {
      if (!isOnline) {
        // T1: Going ONLINE → require GPS permission first
        try {
          await gps.requestGpsPermission();
        } catch (gpsErr) {
          alert(gpsErr.message || 'Impossible de passer en ligne sans la géolocalisation.');
          setTogglingOnline(false);
          return;
        }
        await axios.post(`${API}/api/driver/status/online`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsOnline(true);
        // Start GPS tracking immediately after going online
        gps.startTracking();
      } else {
        // T4: Going OFFLINE → stop tracking cleanly first
        await gps.stopTracking();
        await axios.post(`${API}/api/driver/status/offline`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsOnline(false);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally {
      setTogglingOnline(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Determine sticky button label
  const isOnDetailPage = /\/chauffeur\/missions\/[^/]+/.test(location.pathname);
  const showStickyBtn = activeMission && !isOnDetailPage;
  const isStartable = ['assigned', 'accepted'].includes(activeMission?.status);
  const stickyLabel = isStartable ? 'Démarrer mission' : 'Continuer mission';
  const StickyIcon = isStartable ? PlayCircle : Navigation;

  // Bottom padding accounts for bottom nav + optional sticky button
  const bottomPad = showStickyBtn ? 'pb-[128px]' : 'pb-[72px]';

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex flex-col" data-testid="chauffeur-mobile-layout">
      <DriverPendingOverlay user={user} />

      {/* Session replaced overlay */}
      {sessionReplaced && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col items-center justify-center p-6 text-center" data-testid="session-replaced-overlay">
          <div className="bg-[#1A1A1A] border border-red-500/30 rounded-2xl p-8 max-w-sm w-full space-y-4">
            <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
              <ShieldAlert className="w-7 h-7 text-red-400" />
            </div>
            <h2 className="text-white font-bold text-lg">Session terminée</h2>
            <p className="text-[#E5E5E5]/60 text-sm leading-relaxed">
              Votre compte a été connecté sur un autre appareil. Vous avez été déconnecté de cet appareil.
            </p>
            <div className="flex items-center justify-center gap-1.5 text-[#E5E5E5]/30 text-xs">
              <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              Déconnexion en cours...
            </div>
          </div>
        </div>
      )}

      {/* ── Fixed Header ── */}
      <header
        className="fixed top-0 inset-x-0 z-50 bg-[#0B0B0B]/95 backdrop-blur-md border-b border-[#C9A24D]/15"
        data-testid="chauffeur-header"
      >
        <div className="flex items-center justify-between px-4 h-14 max-w-screen-lg mx-auto">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="font-['Playfair_Display'] text-lg font-bold text-[#C9A24D] shrink-0">Y GO</span>
            <span className="text-[#E5E5E5]/25 hidden xs:inline">|</span>
            <span className="text-[#E5E5E5] text-sm font-medium truncate hidden xs:inline">
              {user?.first_name} {user?.last_name}
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* GPS Indicator (Ticket 7) */}
            <GpsIndicator gpsStatus={gps.gpsStatus} gpsAccuracy={gps.gpsAccuracy} />

            <button
              onClick={toggleOnline}
              disabled={togglingOnline || user?.driver_status !== 'ACTIVE'}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all min-h-[36px] touch-manipulation
                ${isOnline ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}
                disabled:opacity-30 disabled:cursor-not-allowed`}
              data-testid="online-toggle-btn"
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
              {isOnline ? 'En ligne' : 'Hors ligne'}
            </button>

            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-[#E5E5E5]/50 hover:text-[#C9A24D] hover:bg-[#C9A24D]/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              data-testid="logout-button"
            >
              <LogOut className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
      </header>

      {/* GPS Loss Banner (Ticket 8) */}
      {(gps.isLost || gps.gpsStatus === 'error') && <GpsLossBanner onRetry={gps.retryTracking} />}

      {/* Network Banner (Ticket 17) */}
      {!gps.isLost && gps.gpsStatus !== 'error' && network.isWeakNetwork && (
        <NetworkBanner
          isOnline={network.isOnline}
          isSlow={network.isSlow}
          pendingRetries={network.pendingRetries + offlineQueue.queueSize}
          onRetry={() => { network.processQueue(); offlineQueue.flush(); }}
        />
      )}

      {/* T16: Install App Banner (only if not standalone and can install) */}
      {standalone.canInstall && !standalone.isStandalone && (
        <div className="fixed top-14 inset-x-0 z-30 px-4 py-2 bg-[#C9A24D]/95 backdrop-blur-sm" data-testid="install-banner">
          <div className="flex items-center justify-between max-w-screen-lg mx-auto gap-3">
            <p className="text-black text-xs font-medium">Installez Y GO pour un accès rapide</p>
            <button
              onClick={standalone.promptInstall}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-black/20 text-black text-xs font-semibold min-h-[36px] touch-manipulation hover:bg-black/30 transition-colors"
              data-testid="install-app-btn"
            >
              <Download className="w-3 h-3" /> Installer
            </button>
          </div>
        </div>
      )}

      {/* ── Main Content ── */}
      <main className={`flex-1 pt-14 ${gps.isLost || gps.gpsStatus === 'error' || network.isWeakNetwork || (standalone.canInstall && !standalone.isStandalone) ? 'mt-10' : ''} ${bottomPad}`}>
        <Outlet context={{ isOnline, toggleOnline, togglingOnline, activeMission, refreshActiveMission: fetchActiveMission, gps, network, offlineQueue, standalone }} />
      </main>

      {/* ── Ticket 4: Sticky Active Mission Button ── */}
      {showStickyBtn && (
        <div
          className="fixed bottom-[72px] inset-x-0 z-50 px-4 pb-2 pt-2 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/95 to-transparent"
          data-testid="sticky-active-mission"
        >
          <button
            onClick={() => navigate(`/chauffeur/missions/${activeMission.id}`)}
            className="w-full max-w-screen-lg mx-auto flex items-center justify-between gap-3 px-4 py-3 rounded-xl
              bg-[#C9A24D] hover:bg-[#d4af5a] active:bg-[#b8913f] text-black font-semibold
              min-h-[52px] touch-manipulation transition-colors shadow-lg shadow-[#C9A24D]/20"
            data-testid="sticky-mission-btn"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <StickyIcon className="w-5 h-5 shrink-0" />
              <div className="text-left min-w-0">
                <p className="text-sm font-bold leading-tight">{stickyLabel}</p>
                <p className="text-[10px] opacity-70 truncate">
                  {activeMission.booking_ref} · {activeMission.pickup_address || ''}
                </p>
              </div>
            </div>
            <span className="text-xs font-mono opacity-60 shrink-0">
              {activeMission.pickup_datetime
                ? new Date(activeMission.pickup_datetime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                : ''}
            </span>
          </button>
        </div>
      )}

      {/* ── Fixed Bottom Navigation ── */}
      <nav
        className="fixed bottom-0 inset-x-0 z-50 bg-[#111111]/95 backdrop-blur-md border-t border-white/8"
        data-testid="chauffeur-bottom-nav"
      >
        <div className="flex items-stretch max-w-screen-lg mx-auto">
          {NAV_ITEMS.map((item) => {
            // Ticket 13: Disable mission/dispatch tabs for non-ACTIVE drivers
            const disabled = !isActiveDriver && item.to !== '/chauffeur/profil';
            if (disabled) {
              return (
                <div
                  key={item.to}
                  className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-white/15 cursor-not-allowed"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] transition-colors touch-manipulation relative
                  ${isActive ? 'text-[#C9A24D]' : 'text-[#E5E5E5]/40 hover:text-[#E5E5E5]/60 active:text-[#C9A24D]/70'}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                {item.end && pendingProposals > 0 && (
                  <span className="absolute top-1.5 right-1/4 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center animate-pulse"
                    data-testid="pending-proposals-badge">{pendingProposals}</span>
                )}
              </NavLink>
            );
          })}
        </div>
        <div className="h-[env(safe-area-inset-bottom)]" />
      </nav>
    </div>
  );
}
